/**
 * What is actually deployed, at the foot of the sidebar.
 *
 * Two builds, not one, and that is the whole point. The dashboard is a static
 * bundle stamped at `vite build`; the API is a separate image stamped at its own
 * build. They are pushed by the same workflow from the same commit — and they
 * are deployed by two independent Portainer webhooks against two independent
 * stack definitions, so they come apart.
 *
 * They have already come apart in a way that cost a day: one stack was pinned to
 * an old image repository name, so it kept serving a months-stale API while the
 * dashboard beside it was current. Every screen looked right. Nothing anywhere
 * said the two halves disagreed.
 *
 * So this renders the UI commit and the API commit, and when they differ it says
 * so in the one place a person is always looking at. That mismatch line is the
 * feature; the version numbers are the supporting detail.
 */
import { useHealth } from "@/api/hooks";
import { BUILD, formatBuildTime, shortCommit } from "@/lib/build";

function Row({
  label,
  value,
  title,
  tone,
}: {
  label: string;
  value: string;
  title?: string;
  tone?: "muted" | "danger";
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          tone === "danger"
            ? "truncate font-mono text-destructive"
            : "truncate font-mono text-muted-foreground"
        }
        title={title ?? value}
      >
        {value}
      </span>
    </div>
  );
}

export function BuildStamp() {
  const health = useHealth();

  const api = health.data;
  // Unknown until the probe answers, and unknown is NOT a mismatch — claiming a
  // drift because the API is briefly unreachable would train everyone to ignore
  // the line that matters.
  const mismatch =
    api !== undefined && api.commit !== BUILD.commit && BUILD.commit !== "local";

  const apiValue = health.isPending
    ? "…"
    : api
      ? shortCommit(api.commit)
      : "unreachable";

  return (
    <div className="space-y-0.5 border-t border-border pt-3 text-[0.7rem] leading-relaxed">
      <Row
        label="UI"
        value={shortCommit(BUILD.commit)}
        title={`${BUILD.version} · ${BUILD.commit} · built ${formatBuildTime(BUILD.builtAt)}`}
      />
      <Row
        label="API"
        value={apiValue}
        tone={api === undefined && !health.isPending ? "danger" : "muted"}
        title={
          api
            ? `${api.version} · ${api.commit} · built ${formatBuildTime(api.built_at)} · mode ${api.mode}`
            : "GET /v1/health did not answer."
        }
      />
      {mismatch ? (
        <p className="pt-1 text-destructive">
          UI and API are on different builds. One of the two stacks did not
          redeploy.
        </p>
      ) : null}
      {BUILD.commit === "local" ? (
        <p className="pt-1 text-muted-foreground">Local build — not from CI.</p>
      ) : null}
    </div>
  );
}
